import { useQuery } from '@tanstack/react-query';
import { NextSession, nextSessions } from '@uplift/api';
import { formatUserFullName, getUserFullName } from '@uplift/utils';
import { CalendarDateService } from '@uplift/services';
import { useTimezone } from '@uplift/contexts';
import { useAssetsQuery, useUserAddressQuery, useUserContactsQuery, useUserEmergencyContactQuery, useUserInsurancesQuery, useUserMatchesQuery, useUserProfileQuery, useUserQuery } from '@uplift/queries';
import { UserStatus } from '@uplift/types';


const processUpcomingSessions = (sessions: NextSession[], timezone: string) => {
  const groupedByParent = sessions.reduce<{ [key: string]: NextSession }>((prev, next) => {
    if (next.appointmentSeriesId) {
      if (!prev[next.appointmentSeriesId]) {
        return {
          ...prev,
          [next.appointmentSeriesId]: next,
        };
      }
      return prev;
    }
    return {
      ...prev,
      [next.id]: next,
    };
  }, {});

  const resultSessions = Object.values(groupedByParent).map(s => {
    const start = CalendarDateService.date(s.fromDatetime).tz(timezone);

    const providerName = formatUserFullName({
      firstName: s.provider.firstName,
      lastName: s.provider.lastName,
      preferredFirstName: s.provider.preferredName,
    });

    const providerDisplay = getUserFullName(s.provider.firstName, s.provider.lastName, s.provider.preferredName);

    return {
      id: s.id,
      fromDatetime: start.format(),
      type: s.type,
      recurring: s.recurring,
      providerName,
      providerRole: s.provider.medicalRole,
      providerDisplay,
    };
  });

  return resultSessions;
};


export const useUpcomingSessionsQuery = (patientId: string, opts?: { suspense?: boolean; enabled?: boolean }) => {
  const timezone = useTimezone();
  const { data } = useQuery({
    queryKey: [ 'user', patientId, 'upcomingSessions' ],
    queryFn: async () => {
      const upcomingSessionsResponse = await nextSessions({ patient_id: patientId, limit: 999, appointments_per_series: 1 });

      return processUpcomingSessions(upcomingSessionsResponse.data, timezone);
    },
    ...opts,
  });

  return { data };
};


export const usePatientDetails = (patientId: string, opts?: { suspense?: boolean }) => {
  const { data: userData } = useUserQuery(patientId, opts);
  const { data: userProfileData } = useUserProfileQuery(patientId, opts);
  const options = { ...opts, enabled: userData?.status === UserStatus.regular };

  const { data: userContactsData } = useUserContactsQuery(patientId, options);
  const { data: userUpcomingSessionsData } = useUpcomingSessionsQuery(patientId, options);
  const { data: matchesData } = useUserMatchesQuery(patientId, undefined, options);
  const { data: userInsurancesData } = useUserInsurancesQuery(patientId, options);
  const { data: userEmergencyContactData } = useUserEmergencyContactQuery(patientId, options);
  const { data: userAddressData } = useUserAddressQuery(patientId, options);
  const assetsIds = matchesData?.allMatches?.map(m => m.providerProfile.avatarAssetId).filter(Boolean);
  const { data: assetsById } = useAssetsQuery(assetsIds, options);

  return {
    user: userData,
    userProfile: userProfileData,
    contacts: userContactsData?.filter(c => c.value),
    upcomingSessions: userUpcomingSessionsData,
    activeMatches: matchesData?.activeMatches,
    archivedMatches: matchesData?.archivedMatches,
    allMatches: matchesData?.allMatches,
    insurances: userInsurancesData,
    emergencyContact: userEmergencyContactData,
    addressState: userAddressData.state,
    addressCounty: userAddressData.county ?? userAddressData.state?.fullName,
    assetsById: assetsById,
  };
};
